import { KatexAdapter } from "./katex-adapter.service";
import { CalcMemory } from "./calc-memory.service";
import { SerieEntity } from "@arch/domain/entity";

type SetGeneralFormulaParams = {
    serieSelected: SerieEntity;
    element: HTMLElement;
};

/**
 * Classe responsável pelas fórmulas de memória de cálculo
 * traduzindo para o Katex
 */

export class CalcMemoryKatex {
    private KatexAdapter: KatexAdapter;
    private CalcMemory: CalcMemory;

    constructor({
        KatexAdapter,
        CalcMemory
    }: {
        KatexAdapter: KatexAdapter;
        CalcMemory: CalcMemory;
    }) {
        this.KatexAdapter = KatexAdapter;
        this.CalcMemory = CalcMemory;
    }

    /**
     * Verifica se a string possui parênteses ou colchetes
     * @param {String} string
     * @returns {Boolean}
     */
    _hasParenthesesAndBrakets = (string: string): boolean =>
        /([()\[\]])/gim.test(string);

    /**
     * Identifica divisão dentro de uma string
     * @param {String} string
     * @param {Number} index
     * @returns retorna intervalo da divisão
     */

    _identifyDivision(string: string, index: number): string {
        let begin = 0,
            i;
        // Percorre caracteres até encontrar ( (inicio do intervalo de divisão)
        for (i = index; i >= 0; i--) {
            if (string[i] === "(") {
                begin = i;
                break;
            }
        }
        const firstParenthesisIndex = string.indexOf("(") + 1;
        const lastParenthesisIndex = string.lastIndexOf(")");
        const withoutFirstParenthesis = string.substring(
            firstParenthesisIndex,
            lastParenthesisIndex
        );
        const hasAnyParenthesis = this.hasParenthesis(withoutFirstParenthesis);
        const division =
            withoutFirstParenthesis.slice(begin, lastParenthesisIndex) || "";
        if (!withoutFirstParenthesis.includes("/") && !division.includes("/")) {
            return string;
        }
        if (
            !division.includes("/") ||
            (!hasAnyParenthesis && withoutFirstParenthesis.includes("/"))
        ) {
            return withoutFirstParenthesis;
        }
        return division;
    }

    hasParenthesis(str: string): boolean {
        return str.includes("(") && str.includes(")");
    }

    /**
     *
     * @param {String} string
     * Retona divisão em linguagem do Katex
     */
    _divisionKatex(string: string): string {
        const parts = string.split("/");
        if (parts.length < 3) return `\\dfrac{${parts[0]}}{${parts[1]}}`;

        let aux = `\\dfrac{${parts[0]}}`;
        const lastIndex = parts.length - 1;
        const startIndex = 1;

        const _setBracket = (
            index: number,
            position: number,
            bracket: string
        ) => {
            if (index === position) aux += bracket;
        };

        const _removeExtraParentheses = (str: string): string => {
            const openCount = (str.match(/[{(}]/g) || []).length;
            const closeCount = (str.match(/[{)}]/g) || []).length;

            if (openCount > closeCount) {
                str = str.substring(str.indexOf("(") + 1, str.length);
            } else if (closeCount > openCount) {
                str = str.substring(0, str.lastIndexOf(")"));
            }

            return str;
        };

        for (let i = startIndex; i < parts.length; i++) {
            _setBracket(i, startIndex, "{");

            const next = parts[i + 1];
            parts[i] = _removeExtraParentheses(parts[i]);

            if (next) aux += `(\\dfrac{${parts[i]}}`;
            else aux += `{${parts[i]}})`;

            _setBracket(i, lastIndex, "}");
        }

        return aux;
    }

    /**
     * Faz a tradução de symbols de uma string
     * em linguagem do katex
     * @param {String} string
     */
    _symbolsKatex(string: string): string {
        let result = string.replace(/[%]/g, "\\%");
        result = result.replace(
            /\\mkern\\{?([\\d.]+)pt\\}?/g,
            (match, p1) => `\\mkern${p1}mu`
        );
        result = result.replace(/([A-Za-zÀ-ÿ]+)/g, (match) => {
            if (/^\\text\{.*\}$/.test(match)) return match;
            if (/[À-ÿ]/.test(match)) return `\\text{${match}}`;
            return match;
        });

        result = result.replace(/ +/g, "~");
        return result;
    }

    /**
     *
     * @param {String} formula
     * @returns retorna formula traduzida
     */

    _katexTranslate(formula: string): string {
        if (!this._hasParenthesesAndBrakets(formula) && /[/]/g.test(formula)) {
            formula = this._divisionKatex(formula);
        }
        if (this._hasParenthesesAndBrakets(formula)) {
            for (let i = 0; i < formula.length; i++) {
                if (formula[i] === "/") {
                    const normalizedFormula = this._normalizeFormula(formula);
                    const word = this._identifyDivision(normalizedFormula, i);
                    const katexString = this._divisionKatex(word);
                    formula = normalizedFormula.replace(word, katexString);
                }
            }
        }
        return formula;
    }

    _normalizeFormula(formula: string): string {
        let removeParentheses = formula.replace(/\(\-\)|\(\%\)/gi, "");
        let removeExp = removeParentheses.replace(
            /\(\-\)|\(\%\)|Math.ceil/gi,
            ""
        );
        return removeExp;
    }

    /**
     * @public
     * Gera uma fórmula geral a partir da serie
     * @param {String} serieSelected - serie selecionada(atual)
     * @returns {String} - elemento do DOM
     */
    setGeneralFormula({
        serieSelected,
        element
    }: SetGeneralFormulaParams): any {
        try {
            const calcMemory = this.CalcMemory.getExp(serieSelected);
            const formula = this._katexTranslate(calcMemory);
            const formulaWithoutSymbols = this._symbolsKatex(formula);
            return this.KatexAdapter.render(formulaWithoutSymbols, element);
        } catch (err) {
            console.log(`Katex ${err}`);
        }
    }
}
